<template>
  <div>
    <!-- Filter buttons -->
    <div v-if="!disableFilters" class="rounded bg-white mb-4 sm:rounded-lg">
      <div class="flex flex-col justify-between px-2 py-2.5 sm:flex-row sm:px-6">
        <ul class="mb-2 flex flex-row items-center sm:mb-0">
          <li
            v-for="filter in productTypeFilters"
            :key="filter.value"
            @click="filter.isDisabled ? false : updateFilter(filter.value)"
            class="mr-1 cursor-pointer rounded-md px-3 py-1 text-xs transition duration-150 ease-in-out hover:bg-gray-50"
            :class="{
              'bg-gray-100': filter.value === currentFilter,
              'opacity-50 !cursor-not-allowed': filter.isDisabled
            }"
          >
            {{ filter.label }}
          </li>
        </ul>
      </div>
    </div>

    <!-- ag-grid table -->
    <ag-grid-vue
      class="ag-theme-quartz w-full"
      :style="rows < 20 ? 'height: 600px;' : 'height: calc(100vh - 200px)'"
      :columnDefs="columnDefs"
      :rowData="products"
      :defaultColDef="defaultColDef"
      :context="context"
      :loading="loading"
      :pagination="true"
      :paginationPageSize="rows"
      :paginationPageSizeSelector="false"
      :modules="modules"
      @grid-ready="onGridReady"
      :enterNavigatesVertically="true"
      :enterNavigatesVerticallyAfterEdit="true"
      :undoRedoCellEditing="true"
      :undoRedoCellEditingLimit="10"
      :detailRowAutoHeight="true"
      :rowSelection="disableRowSelection ? null : 'multiple'"
      :suppressRowClickSelection="!disableRowSelection"
      :checkboxSelection="!disableRowSelection"
      :rowHeight="57"
      :sideBar="{
            toolPanels: [
              {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel'
              }
            ]
          }"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import { tags } from '@/helpers/productTags';

export default {
  name: 'ProductsTable',
  components: {
  },
  props: {
    products: {
      type: Array,
      default: () => []
    },
    rows: {
      type: Number,
      default: 25
    },
    loading: {
      type: Boolean,
      default: false
    },
    productTypeFilters: {
      type: Array,
      default: () => [
        {
          label: 'All',
          value: 'all'
        },
        {
          label: 'Products',
          value: 'product'
        },
        {
          label: 'Modifiers',
          value: 'modifier'
        },
        {
          label: 'Modifier Groups',
          value: 'modifier_group'
        }
      ]
    },
    currentFilter: {
      type: String,
      default: 'all'
    },
    disableFilters: {
      type: Boolean,
      default: false
    },
    disableRowSelection: {
      type: Boolean,
      default: false
    }
  },
  async created() {
    // Dynamically import AG Grid
    try {
      const agGridModule = await import('@/plugins/ag-grid');
      const agGrid = await agGridModule.default();
      Vue.use(agGrid);
      
      // Register the component locally after it's loaded
      this.$options.components.AgGridVue = agGrid.AgGridVue;
    } catch (error) {
      alert('Failed to load AG Grid');
    }
  },
  data() {
    return {
      columnDefs: [
        {
          headerName: 'Product',
          checkboxSelection: true,
          field: 'name',
          filter: true,
          floatingFilter: true,
          sortable: true,
          minWidth: 150,
          cellRenderer: params => {
            const imageContainer = document.createElement('div');
            imageContainer.className = 'flex items-center py-2';

            // Image part
            if (params.data && params.data.image) {
              const img = document.createElement('img');
              img.src = params.data.image;
              img.className = 'w-10 h-10 object-cover rounded mr-2';
              imageContainer.appendChild(img);
            } else {
              // Placeholder for missing image
              const placeholder = document.createElement('div');
              placeholder.className = 'w-10 h-10 bg-gray-200 rounded mr-2 flex items-center justify-center';
              placeholder.innerHTML = '<svg class="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>';
              imageContainer.appendChild(placeholder);
            }

            // Create a container for name and PLU to stack them vertically
            if (params.data) {
              const textContainer = document.createElement('div');
              textContainer.className = 'flex flex-col';

              // Name part
              const nameSpan = document.createElement('span');
              nameSpan.className = ' text-sm text-gray-900 cursor-pointer leading-2 hover:text-indigo-600';
              nameSpan.textContent = params.data.name || '';
              nameSpan.addEventListener('click', () => {
                this.editProduct(params.data);
              });

              // PLU part - now added to the column container
              const pluSpan = document.createElement('span');
              pluSpan.className = 'text-xs text-gray-400';
              pluSpan.textContent = params.data.plu || '';

              // Add both elements to the text container
              textContainer.appendChild(nameSpan);
              textContainer.appendChild(pluSpan);

              // Add the text container to the image container
              imageContainer.appendChild(textContainer);
            }

            return imageContainer;
          }
        },
        {
          headerName: 'Type',
          field: 'type',
          filter: true,
          sortable: true,
          minWidth: 120,
          cellRenderer: params => {
            const typeContainer = document.createElement('div');
            typeContainer.className = 'flex items-center mt-4';
            
            if (params.data) {
              let typeValue = params.data.type;
              let bgColorClass = 'bg-gray-100 text-gray-800'; // Default

              // Set different colors based on type
              if (typeValue === 'product') {
                bgColorClass = 'bg-sk-black text-white'; // Black for product
              } else if (typeValue === 'modifier_group' || typeValue.includes('group')) {
                bgColorClass = 'bg-indigo-100 text-indigo-800'; // Indigo for modifier group
              } else if (typeValue === 'modifier') {
                bgColorClass = 'bg-green-100 text-green-800'; // Green for modifier
              }
              
              const chip = document.createElement('span');
              chip.className = `inline-flex items-center px-3 py-1 rounded-full text-xs  ${bgColorClass}`;
              chip.textContent = params.value || 'Product';
              typeContainer.appendChild(chip);
            }

            const posChip = document.createElement('span');
            posChip.className = 'inline-flex items-center px-3 py-1 rounded-md text-xs bg-gray-50 text-gray-800 ml-2';

            const posImg = document.createElement('img');
            posImg.className = 'mr-2';
            posImg.src = 'https://ucarecdn.com/72a08221-873b-46a5-9d58-ce417be32057/-/format/auto/-/resize/200x/';
            posImg.alt = 'Tissl POS Logo';
            posImg.width = 16;
            posImg.height = 16;

            posChip.appendChild(posImg);

            const posText = document.createTextNode('Tissl');
            posChip.appendChild(posText);

            typeContainer.appendChild(posChip);


            return typeContainer;
          }
        },
        {
          headerName: 'Price',
          field: 'price',
          filter: true,
          sortable: true,
          minWidth: 120,
          valueFormatter: params => {
            const rawValue = parseInt(params.value, 10) || 0;
            const displayValue = rawValue / 100;
            return this.$options.filters.currency(displayValue);
          }
        },
        {
          headerName: 'Tags',
          field: 'tags',
          filter: true,
          sortable: true,
          minWidth: 150,
          hide: true,
          cellRenderer: params => {
            const tagsContainer = document.createElement('div');
            tagsContainer.className = 'flex items-center flex-wrap gap-1';
            
            if (params.data && params.data.tags && params.data.tags.length) {
              // Filter tags that are not allergens (values 0-99 and 1000-1129)
              const productTags = params.data.tags.filter(tagValue => 
                (tagValue >= 0 && tagValue <= 99) || (tagValue >= 1000 && tagValue <= 1129)
              );
              
              if (productTags.length === 0) {
                return tagsContainer;
              }
              
              // Create a tooltip container for hover
              const tooltipContainer = document.createElement('div');
              tooltipContainer.className = 'relative inline-block';
              
              // Create visible chips (max 3)
              const visibleCount = Math.min(productTags.length, 3);
              for (let i = 0; i < visibleCount; i++) {
                const tagValue = productTags[i];
                const tagInfo = tags.find(t => t.value === tagValue);
                
                if (tagInfo) {
                  const chip = document.createElement('span');
                  chip.className = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800';
                  chip.textContent = tagInfo.label;
                  tooltipContainer.appendChild(chip);
                }
              }
              
              // Add "more" indicator if needed
              if (productTags.length > 3) {
                const moreChip = document.createElement('span');
                moreChip.className = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 cursor-pointer';
                moreChip.textContent = `+${productTags.length - 3} more`;
                tooltipContainer.appendChild(moreChip);
                
                // Create tooltip content that shows on hover
                const tooltip = document.createElement('div');
                tooltip.className = 'absolute z-10 hidden group-hover:block bg-white p-2 rounded shadow-lg border border-gray-200 min-w-48';
                tooltip.style.bottom = '100%';
                tooltip.style.left = '0';
                
                // Add all tags to tooltip
                productTags.forEach(tagValue => {
                  const tagInfo = tags.find(t => t.value === tagValue);
                  if (tagInfo) {
                    const tooltipItem = document.createElement('div');
                    tooltipItem.className = 'py-1 px-2 text-xs';
                    tooltipItem.textContent = tagInfo.label;
                    tooltip.appendChild(tooltipItem);
                  }
                });
                
                // Make the container a hover group
                tooltipContainer.className += ' group';
                tooltipContainer.appendChild(tooltip);
              }
              
              tagsContainer.appendChild(tooltipContainer);
            }
            
            return tagsContainer;
          }
        },
        {
          headerName: 'Allergens',
          field: 'tags',
          filter: true,
          sortable: true,
          minWidth: 150,
          hide: true,
          cellRenderer: params => {
            const allergensContainer = document.createElement('div');
            allergensContainer.className = 'flex items-center flex-wrap gap-1';
            
            if (params.data && params.data.tags && params.data.tags.length) {
              // Filter allergen tags (values 100-999)
              const allergenTags = params.data.tags.filter(tagValue => 
                tagValue >= 100 && tagValue <= 999
              );
              
              if (allergenTags.length === 0) {
                return allergensContainer;
              }
              
              // Create a tooltip container for hover
              const tooltipContainer = document.createElement('div');
              tooltipContainer.className = 'relative inline-block';
              
              // Create visible chips (max 3)
              const visibleCount = Math.min(allergenTags.length, 3);
              for (let i = 0; i < visibleCount; i++) {
                const tagValue = allergenTags[i];
                const tagInfo = tags.find(t => t.value === tagValue);
                
                if (tagInfo) {
                  const chip = document.createElement('span');
                  chip.className = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800';
                  chip.textContent = tagInfo.label;
                  tooltipContainer.appendChild(chip);
                }
              }
              
              // Add "more" indicator if needed
              if (allergenTags.length > 3) {
                const moreChip = document.createElement('span');
                moreChip.className = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 cursor-pointer';
                moreChip.textContent = `+${allergenTags.length - 3} more`;
                tooltipContainer.appendChild(moreChip);
                
                // Create tooltip content that shows on hover
                const tooltip = document.createElement('div');
                tooltip.className = 'absolute z-10 hidden group-hover:block bg-white p-2 rounded shadow-lg border border-gray-200 min-w-48';
                tooltip.style.bottom = '100%';
                tooltip.style.left = '0';
                
                // Add all allergens to tooltip
                allergenTags.forEach(tagValue => {
                  const tagInfo = tags.find(t => t.value === tagValue);
                  if (tagInfo) {
                    const tooltipItem = document.createElement('div');
                    tooltipItem.className = 'py-1 px-2 text-xs';
                    tooltipItem.textContent = tagInfo.label;
                    tooltip.appendChild(tooltipItem);
                  }
                });
                
                // Make the container a hover group
                tooltipContainer.className += ' group';
                tooltipContainer.appendChild(tooltip);
              }
              
              allergensContainer.appendChild(tooltipContainer);
            }
            
            return allergensContainer;
          }
        }
      ],
      defaultColDef: {
        resizable: true,
        suppressMovable: true,
        flex: 1,
        minWidth: 100,
        autoHeight: true
      },
      context: { componentParent: this },
      modules: [],
      gridApi: null,
      columnApi: null
    };
  },
  methods: {
    onGridReady(params) {
      this.gridApi = params.api;
      this.columnApi = params.columnApi;
      params.api.sizeColumnsToFit();
      this.$emit('grid-ready', params);
    },
    updateFilter(filterValue) {
      this.gridApi.showLoadingOverlay()
      this.$emit('filter-changed', filterValue);
    },
    editProduct(product) {
      this.$emit('edit-product', product);
    }
  }
};
</script>

<style>
.ag-theme-quartz {
  --ag-header-height: 40px;
  --ag-header-foreground-color: #374151;
  --ag-header-background-color: #F9FAFB;
  --ag-header-cell-hover-background-color: #F3F4F6;
  --ag-row-hover-color: #F3F4F6;
  --ag-selected-row-background-color: rgba(99, 102, 241, 0.1);
  --ag-font-size: 14px;
  --ag-font-family: inherit;
}
</style>
